import { useState, useEffect, useContext } from "react";
import { TokenizationProps } from "../props";
import { TokenizationKind } from "../model/structure";
import { SpanTree } from "./span/span-tree";
import {
	EntityMentionSet,
	EntityMention as EntityMentionModel,
} from "../model/entities";
import {
	SituationMentionSet,
	SituationMention as SituationMentionModel,
} from "../model/situations";
import { TaggedTokenSpan as TaggedTokenSpanModel } from "../structure/span/span-tree";
import { v4 as uuidv4 } from "uuid";
import { UUID } from "../model/uuid";
import { Token } from "../structure/token/token";
import { TokenNode } from "../structure/span/span-tree";
import { SpanNode } from "../structure/span/span-tree";
import { TaggedTokenSpan } from "../structure/span/tagged-span";
import TextAnnotation from "../annotation/text";
import { TokenTaggingAnnotation } from "../annotation/token-tagging";
import {
	SpanTreeContext,
	TokenizationContext,
	MentionModelContext,
	MouseDragTokenContext,
} from "../contexts";
import {
	AnnotationSubKind,
	EntityMentionAnnotationScheme,
	SituationMentionAnnotationScheme,
} from "../annotation/scheme";
import { EntityMention } from "../model/entities";
import { MentionModel as SpanMentionModel } from "../communication";

type MentionAnnotationScheme =
	| EntityMentionAnnotationScheme
	| SituationMentionAnnotationScheme;
/** A react component corresponding to a concrete tokenization */
export function Tokenization(props: TokenizationProps) {
	const [spanTree, setSpanTree] = useState<SpanTree | undefined>(undefined);
	const [mouseDragging, setMouseDragging] = useState<boolean | undefined>(
		undefined
	);
	const [shiftMouseDragging, setShiftMouseDragging] = useState<
		boolean | undefined
	>(undefined);
	const [dragStartTokenIndex, setDragStartTokenIndex] = useState<number>(-1);
	const [dragEndTokenIndex, setDragEndTokenIndex] = useState<number>(-1);

	/** useState to re-render everytime there is method call for spanTree object  TODO: review */
	const [state, setState] = useState<number>(0);

	// let activeEntityMentions: UUID[] = [];

	/** useContext to get the annotation model i.e. entity/situation from provider at communication */
	const mentionModel = useContext(MentionModelContext);

	const map = useContext(SpanTreeContext);
	const {
		highlightedMentions,
		activateStructureIds,
		activeTypeIds,
		entityMentionSetList,
		situationMentionSetList,
		selectionBox,
		addToSpanMap,
		deleteFromSpanMap,
		setDeleteSpanCallback,
		metadata,
		activeSituation,
		activateSituationId,
		deactivateStructureIds,
		spanToTypeMap,
		setSpanToTypeMap,
		setSituationArgumentMap,
		metaKeyClicked,
		shiftKeyClicked,
		addToUserInteractions,
		spanMap,
		hoveredSpan,
		setHoveredSpan,
	} = useContext(TokenizationContext);

	useEffect(() => {
		if (spanTree) {
			map.set(props.uuid.uuidString, spanTree);
		}
	}, [spanTree]);

	useEffect(() => {
		setSpanTree(_initSpanTree());
	}, [metadata]);

	useEffect(() => {
		// NEEDS REVIEW
		if (props.activeStructureIds.length > 0) {
			const activeSpanId = props.activeStructureIds[0][0];
			const exists = spanTree?._findSpanNodes(activeSpanId).length;
			const callback = () => {
				const activeSpanId = props.activeStructureIds[0][0];
				spanTree?._deleteSpan(activeSpanId);
			};
			if (exists) {
				setDeleteSpanCallback(() => {
					return callback;
				});
			}
		}
	}, [props.activeStructureIds]);

	useEffect(() => {
		if (mouseDragging === false) {
			const mentionType = mentionModel;

			const range = spanTree!.dragSelectTokenRange(
				dragStartTokenIndex,
				dragEndTokenIndex
			);
			const spanId = { uuidString: uuidv4() };

			const spans = spanTree?.nodes
				.filter((node) => node.kind === "span")
				.map((node) => {
					const newNode = node as SpanNode;
					return newNode.span;
				});
			const tokens = spanTree?.nodes
				.filter((node) => node.kind === "token")
				.map((node) => {
					const newNode = node as TokenNode;
					return newNode.token;
				});

			const newSpanTree = new SpanTree(tokens || [], spans || []);

			newSpanTree?.createSpanFromToken(
				props.uuid,
				spanId,
				range[0],
				"",
				mentionType
			);

			for (var i = 1; i < range.length; i++) {
				newSpanTree?.addTokenToSpan(
					props.uuid,
					spanId,
					range[i],
					"",
					mentionType
				);
			}

			const tokenIndexList =
				newSpanTree._findSpanNodes(spanId)[0].span.tokenIndexList;
			const text = _spanText(tokenIndexList);
			if (text && addToSpanMap) {
				addToSpanMap(spanId, text);
			}

			addToUserInteractions("create", {
				type:
					mentionModel === "entity"
						? "entityMention"
						: "situationMention",
				text: text,
			});

			setSpanTree(newSpanTree);
			_activateSpan(spanId, mentionType);

			setDragStartTokenIndex(-1);
			setDragEndTokenIndex(-1);
		}
	}, [mouseDragging]);

	useEffect(() => {
		if (shiftMouseDragging === false) {
			const activeSpanId = _getActiveSpanId();
			const activeTokenizationId = _getActiveTokenizationId();
			const mentionType = mentionModel;

			if (
				activeSpanId !== null &&
				activeTokenizationId?.uuidString === props.uuid.uuidString
			) {
				const range = spanTree!.dragSelectTokenRange(
					dragStartTokenIndex,
					dragEndTokenIndex
				);

				const spans = spanTree?.nodes
					.filter((node) => node.kind === "span")
					.map((node) => {
						const newNode = node as SpanNode;
						return newNode.span;
					});
				const tokens = spanTree?.nodes
					.filter((node) => node.kind === "token")
					.map((node) => {
						const newNode = node as TokenNode;
						return newNode.token;
					});

				const newSpanTree = new SpanTree(tokens || [], spans || []);

				for (var i of range) {
					newSpanTree.addTokenToSpan(
						props.uuid,
						activeSpanId,
						i,
						"",
						mentionType
					);
				}

				const tokenSet = new Set<number>();
				newSpanTree
					._findSpanNodes(activeSpanId)
					.forEach((node, index) => {
						// tokenSet.add(...node.span.tokenIndexList)
						node.span.tokenIndexList.forEach(
							tokenSet.add,
							tokenSet
						);
					});
				// Array Conversion sorts it apparently
				const tokenIndexList = Array.from(tokenSet);

				const text = _spanText(tokenIndexList);

				// addToUserInteractions(
				// 	`Extended span '${spanMap.get(
				// 		activeSpanId.uuidString
				// 	)}' to '${text}'`
				// );

				addToUserInteractions("create", {
					type:
						mentionModel === "entity"
							? "entityMention"
							: "situationMention",
					text: text,
				});

				if (text && addToSpanMap) {
					addToSpanMap(activeSpanId, text);
				}

				setSpanTree(newSpanTree);
				_activateSpan(activeSpanId, mentionType);
			}

			setDragStartTokenIndex(-1);
			setDragEndTokenIndex(-1);
		}
	}, [shiftMouseDragging]);

	/** type check for Referent Model */
	type MentionModel = EntityMentionModel | SituationMentionModel;

	/**
	 * Function to return populated span or empty span based on constraints.
	 *
	 * @param model
	 * ReferentModel object populated with span properties.
	 * @returns
	 * Populated/empty span.
	 */
	function TaggedTokenSpanProps(model: MentionModel): TaggedTokenSpanModel {
		const emptySpan = {
			spanId: { uuidString: uuidv4() },
			tokenizationId: { uuidString: uuidv4() },
			tokenIndexList: [],
			type: "",
			mentionModel: mentionModel,
		};

		if (model.tokens === undefined) {
			return emptySpan;
		} else if ("entityType" in model) {
			return {
				spanId: model.uuid,
				tokenizationId: model.tokens.tokenizationId,
				tokenIndexList: model.tokens.tokenIndexList,
				type: model.entityType === undefined ? "" : model.entityType,
				mentionModel: "entity",
			};
		} else if ("situationType" in model) {
			return {
				spanId: model.uuid,
				tokenizationId: model.tokens.tokenizationId,
				tokenIndexList: model.tokens.tokenIndexList,

				type:
					model.situationType === undefined
						? ""
						: model.situationType,
				mentionModel: "situation",
			};
		} else {
			return emptySpan;
		}
	}

	/** Function to get activeSpanId */
	// REVIEW THIS
	function _getActiveSpanId() {
		let activeSpanId = props.activeStructureIds.length
			? props.activeStructureIds[0][0]
			: null;
		return activeSpanId;
	}

	/** Function to get activeTokenizationId */
	function _getActiveTokenizationId() {
		let activeTokenizationId = props.activeStructureIds.length
			? props.activeStructureIds[0][1]
			: null;
		return activeTokenizationId;
	}

	/** Function to get activeSpanId */
	function _getActiveSituation() {
		return activeSituation.length ? activeSituation[0] : null;
	}

	/** Function to initialize taggedTokenSpanList */
	function _initTaggedTokenSpanList() {
		const spanList: TaggedTokenSpanModel[] = [];

		const _spanToTypeMap: Map<string, string> = new Map<string, string>();
		const _situationArgumentMap: Map<
			string,
			Map<string, Set<string>>
		> = new Map<string, Map<string, Set<string>>>();

		if (entityMentionSetList !== undefined) {
			const mentionSet = entityMentionSetList.find(
				(elem: EntityMentionSet) =>
					elem.metadata?.tool === metadata?.tool &&
					elem.metadata?.timestamp === metadata?.timestamp
			);

			if (
				mentionSet !== undefined &&
				mentionSet.mentionList !== undefined
			) {
				const map: TaggedTokenSpanModel[] = [];
				mentionSet.mentionList.forEach((entityMention) => {
					const span = TaggedTokenSpanProps(entityMention);
					map.push(span);
					if (span.type)
						_spanToTypeMap.set(span.spanId.uuidString, span.type);
				});
				const set = map.filter(
					(span) =>
						span.tokenizationId.uuidString === props.uuid.uuidString
				);
				for (const span of set) {
					addToSpanMap(span.spanId, _spanText(span.tokenIndexList));
				}
				spanList.push(...set);
			}
		}

		if (situationMentionSetList !== undefined) {
			const mentionSet = situationMentionSetList.find(
				(elem: SituationMentionSet) =>
					elem.metadata?.tool === metadata?.tool &&
					elem.metadata?.timestamp === metadata?.timestamp
			);

			if (
				mentionSet !== undefined &&
				mentionSet.mentionList !== undefined
			) {
				// const map = mentionSet.mentionList.map(TaggedTokenSpanProps);
				const map: TaggedTokenSpanModel[] = [];
				mentionSet.mentionList.forEach((situationMention) => {
					const span = TaggedTokenSpanProps(situationMention);
					map.push(span);
					if (span.type)
						_spanToTypeMap.set(span.spanId.uuidString, span.type);
					if (
						situationMention.argumentList &&
						situationMention.argumentList.length
					) {
						situationMention.argumentList.forEach((arg) => {
							if (
								!_situationArgumentMap.has(
									situationMention.uuid.uuidString
								)
							)
								_situationArgumentMap.set(
									situationMention.uuid.uuidString,
									new Map<string, Set<string>>()
								);
							const _situationMentionArgumentList =
								_situationArgumentMap.get(
									situationMention.uuid.uuidString
								);

							if (
								!_situationMentionArgumentList?.has(
									arg.role || ""
								)
							) {
								_situationMentionArgumentList?.set(
									arg.role || "",
									new Set<string>()
								);
							}

							if (arg.tokens) {
								const taggedSpan: EntityMention = {
									uuid: {
										uuidString: uuidv4(),
									},
									tokens: arg.tokens,
									entityType: "",
								};
								map.push(TaggedTokenSpanProps(taggedSpan));
								_situationMentionArgumentList
									?.get(arg.role || "")
									?.add(taggedSpan.uuid.uuidString);
							} else if (arg.situationMentionId) {
								_situationMentionArgumentList
									?.get(arg.role || "")
									?.add(arg.situationMentionId.uuidString);
							} else if (arg.entityMentionId) {
								_situationMentionArgumentList
									?.get(arg.role || "")
									?.add(arg.entityMentionId.uuidString);
							}
						});
					}
				});
				const set = map.filter(
					(span) =>
						span.tokenizationId.uuidString === props.uuid.uuidString
				);
				for (const span of set) {
					addToSpanMap(span.spanId, _spanText(span.tokenIndexList));
				}
				spanList.push(...set);
			}
		}

		setSpanToTypeMap(_spanToTypeMap);
		setSituationArgumentMap(_situationArgumentMap);

		return spanList;
	}

	/** Function to initialize spanTree */
	function _initSpanTree() {
		const taggedTokenSpanList = _initTaggedTokenSpanList();

		if (props.tokenList) {
			const spanTree = new SpanTree(
				props.tokenList.tokenList,
				taggedTokenSpanList
			);
			return spanTree;
		}
	}

	function _spanText(tokenIndex: number[]): string {
		// Check this
		tokenIndex.sort((a: number, b: number) => a - b);
		let text: string =
			props.tokenList?.tokenList[tokenIndex[0]]?.text + " ";
		for (var i = 1; i < tokenIndex.length; i++) {
			// text += props.tokenList.TokenList[i].text + " "
			if (tokenIndex[i] - tokenIndex[i - 1] > 1) {
				text += "... ";
			}
			text += props.tokenList?.tokenList[tokenIndex[i]].text + " ";
		}
		return text.slice(0, -1);
	}

	/** Activate a span by its UUID
	 *
	 * All other spans are deactivated prior to activation, meaning
	 * that only a single span may be active for this annotation scheme.
	 *
	 * @param spanId
	 * The UUID of the span to activate
	 */
	function _activateSpan(spanId: UUID, spanModel: SpanMentionModel) {
		activateStructureIds([{ ...spanId }], [props.uuid], spanModel);
	}

	/** Activate a span by its UUID
	 *
	 * All other spans are deactivated prior to activation, meaning
	 * that only a single span may be active for this annotation scheme.
	 *
	 * @param spanId
	 * The UUID of the span to activate
	 */
	function _activateSituation(spanId: UUID, tokenizationId: UUID) {
		// activateSituationId([{ ...spanId }], [props.uuid], true);
		deactivateStructureIds();
		activateSituationId([{ ...spanId }, { ...tokenizationId }]);
	}

	/**
	 * Function to get the value of type in ontology
	 * @param typeId
	 * uuid of type specified in Ontology.
	 * @returns
	 * typeId value.
	 */
	function _getTypeValue(typeId: UUID | null) {
		if (typeId === null) {
			return "";
		} else {
			const type = props.annotationScheme.ontology?.types.find(
				(t) => t.uuid.uuidString === typeId.uuidString
			);

			if (type === undefined) {
				return "";
			} else {
				return type.value;
			}
		}
	}

	/**
	 * Function to handle mouse events on the interface such as
	 * create span, add to a span.
	 */
	function handleTokenClick(
		tokenIndex: number,
		typeId: UUID | null,
		e: React.MouseEvent
	) {
		let activeSpanId = _getActiveSpanId();
		const isPresent = spanTree?.nodes.find(
			(element) =>
				element.kind === "span" &&
				element.span.spanId.uuidString === activeSpanId?.uuidString
		);
		if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
			e.stopPropagation();
			activeSpanId = { uuidString: uuidv4() };
			const spans = spanTree?.nodes
				.filter((node) => node.kind === "span")
				.map((node) => {
					const newNode = node as SpanNode;
					return newNode.span;
				});
			const tokens = spanTree?.nodes
				.filter((node) => node.kind === "token")
				.map((node) => {
					const newNode = node as TokenNode;
					return newNode.token;
				});
			const newSpanTree = new SpanTree(tokens || [], spans || []);
			const mentionType = mentionModel;
			newSpanTree?.createSpanFromToken(
				props.uuid,
				activeSpanId,
				tokenIndex,
				"",
				mentionType
			);
			const text = props.tokenList?.tokenList[tokenIndex].text;
			if (text && addToSpanMap) {
				addToSpanMap(activeSpanId, text);
			}
			setSpanTree(newSpanTree);
			setState((state) => state + 1);
			_activateSpan(activeSpanId, mentionType);
		} else if (
			(e.metaKey || e.ctrlKey) &&
			e.shiftKey &&
			activeSpanId !== null &&
			isPresent
		) {
			e.stopPropagation();
			let current: number[] = [];
			const mentionType = mentionModel;
			spanTree?.addTokenToSpan(
				props.uuid,
				activeSpanId,
				tokenIndex,
				_getTypeValue(typeId),
				mentionType
			);
			if (spanTree)
				for (const span of spanTree.nodes) {
					if (
						span.kind === "span" &&
						span.span.spanId.uuidString === activeSpanId.uuidString
					) {
						current.push(...span.tokenIndexList);
					}
				}
			if (deleteFromSpanMap) deleteFromSpanMap(activeSpanId);
			current = Array.from(new Set(current)).sort((a, b) => {
				return a - b;
			});
			const text = _spanText(current);
			if (text && addToSpanMap) {
				addToSpanMap(activeSpanId, text);
			}
			_activateSpan(activeSpanId, mentionType);
			setState((value) => value + 1);
		} else {
			console.error("Cannot find active span");
			// throw "Cannot find active span";
		}
	}

	/** Function to handle mouse events on the interface such as merge spans */
	function handleSpanClick(
		span: SpanNode,
		typeId: UUID | null,
		e?: React.MouseEvent
	) {
		const spanId = span.spanId;
		const spanType = span.span.mentionModel!;
		const tokenizationId = span.tokenizationId;
		if (e !== undefined && !(e.metaKey || e.ctrlKey)) {
			e.stopPropagation();
			// if(activeSituation.length === 0 && annotationTask.kind==="mention" && annotationTask.subkind==="situation"){
			// 	_activateSituation(spanId,tokenizationId);
			// } else {
			// 	if(activeSituation.length && activeSituation[0].uuidString === spanId.uuidString){
			// 		// pass
			// 	} else{
			// 		_activateSpan(spanId);
			// 	}
			// }
			// if( annotationTask.kind === "mention" &&
			// 	annotationTask.subkind === "situation" &&
			// 	props.activeStructureIds.length &&
			// 	span.mentionModel === "situation" &&
			// 	span.type !== ""  &&
			// 	props.activeStructureIds[0].uuidString === spanId.uuidString){
			// 	_activateSituation(spanId,tokenizationId);
			// } else {
			// 	_activateSpan(spanId);
			// }
			if (
				!activeSituation.length ||
				(activeSituation.length && activeSituation[0] !== spanId)
			)
				_activateSpan(spanId, spanType);
		} else {
			const activeSpanId = _getActiveSpanId();
			if (e === undefined && typeId !== null) {
				// update this span's type
				spanTree?.updateType(spanId, _getTypeValue(typeId));
			} else if (
				e !== undefined &&
				(e.metaKey || e.ctrlKey) &&
				!e.shiftKey &&
				typeId !== null
			) {
				// update this span's type
				spanTree?.updateType(spanId, _getTypeValue(typeId));
			}
			// Merge spans cases decided to be removed for now.
			// else if (
			// 	e !== undefined &&
			// 	(e.metaKey || e.ctrlKey) &&
			// 	e.shiftKey &&
			// 	activeSpanId !== null
			// ) {
			// 	if(props.spanMap){
			// 		const a = spanTree?.nodes.find((element) => element.kind === "span" && element.span.spanId.uuidString === spanId.uuidString)?.tokenIndexList
			// 		const b = spanTree?.nodes.find((element) => element.kind === "span" && element.span.spanId.uuidString === activeSpanId.uuidString)?.tokenIndexList
			// 		let newTokenList :number[]= []
			// 		if (a !== undefined && b!== undefined){
			// 			newTokenList = Array.from(new Set((a.concat(b)))).sort()
			// 		}
			// 		// let newTokenList = Array.from(new Set(a.concat(b)))
			// 		// console.log(newTokenList)
			// 		props.spanMap?.delete(spanId.uuidString)
			// 		props.spanMap?.delete(activeSpanId.uuidString)
			// 		props.spanMap?.set(activeSpanId.uuidString,_spanText(newTokenList))
			// 	}
			// 	spanTree?.mergeSpans(spanId, activeSpanId);
			// 	_activateSpan(spanId);
			// }
			// activate the span
			setState((value) => value + 1);
		}
	}

	/**
	 * Function to render spanNodes.
	 * @param tree
	 * spanTree where the node is present.
	 * @param node
	 * node to be rendered.
	 * @returns
	 * TaggedTokenSpan object with node properties.
	 */
	function renderSpanNode(spanTree: SpanTree, node: SpanNode) {
		const activeSpanId = _getActiveSpanId();
		const activeTypeId = activeTypeIds.length > 0 ? activeTypeIds[0] : null;

		const activeSituationId = _getActiveSituation();

		return (
			<TaggedTokenSpan
				key={node.subspanId.uuidString}
				annotationScheme={
					props.annotationScheme as MentionAnnotationScheme
				}
				activeSpanId={activeSpanId}
				activeTypeId={activeTypeId}
				activeSituation={activeSituationId}
				highlightMentions={
					props.activeStructureIds.length === 0 &&
					highlightedMentions.size
						? highlightedMentions
						: new Set<string>()
				}
				tree={spanTree}
				node={node}
				nodeMentionModel={node.mentionModel}
				onSpanClick={handleSpanClick}
				onTokenClick={handleTokenClick}
				metaKeyClicked={metaKeyClicked}
				shiftKeyClicked={shiftKeyClicked}
				currentMentionModel={mentionModel}
				hoveredSpan={hoveredSpan}
				setHoveredSpan={setHoveredSpan}
			/>
		);
	}

	/**
	 * Function to render Token Nodes.
	 * @param spanTree
	 * spanTree where the node to be rendered is present.
	 * @param node
	 * Token node to be rendered.
	 * @returns
	 * Token object with node properties.
	 */
	function renderTokenNode(spanTree: SpanTree, node: TokenNode) {
		const activeTypeId = activeTypeIds.length > 0 ? activeTypeIds[0] : null;

		return (
			<Token
				key={node.token.tokenIndex}
				onClick={(e: React.MouseEvent) =>
					handleTokenClick(node.tokenIndex, activeTypeId, e)
				}
				unhighlight={
					props.activeStructureIds.length === 0 &&
					activeTypeIds.length !== 0
				}
				metaKeyClicked={metaKeyClicked}
				shiftKeyClicked={shiftKeyClicked}
				currentMentionModel={mentionModel}
				hoveredSpan={hoveredSpan}
				setHoveredSpan={setHoveredSpan}
				{...node.token}
			/>
		);
	}

	/** Function to render list of nodes. */
	function renderList() {
		if (props.tokenList) {
			if (props.annotationScheme.kind === "text") {
				return (
					<TextAnnotation
						annotationScheme={props.annotationScheme}
						activeTypeIds={activeTypeIds}
						tokenList={props.tokenList.tokenList}
					/>
				);
			} else if (props.annotationScheme.kind === "tagging") {
				if (props.tokenTaggingList !== undefined) {
					return (
						<TokenTaggingAnnotation
							annotationScheme={props.annotationScheme}
							activeTypeIds={activeTypeIds}
							activeStructureIds={props.activeStructureIds}
							activateStructureIds={activateStructureIds}
							selectionBox={selectionBox}
							tokenList={props.tokenList.tokenList}
							tokenTaggingList={props.tokenTaggingList}
						/>
					);
				} else {
					return (
						<TokenTaggingAnnotation
							annotationScheme={props.annotationScheme}
							activeTypeIds={activeTypeIds}
							activeStructureIds={props.activeStructureIds}
							activateStructureIds={activateStructureIds}
							selectionBox={selectionBox}
							tokenList={props.tokenList.tokenList}
						/>
					);
				}
			}
		}

		const elements = spanTree?.maxima.map((n) =>
			n.kind === "span"
				? renderSpanNode(spanTree, n)
				: renderTokenNode(spanTree, n)
		);

		// return <span>{elements}</span>;
		return <>{elements}</>;
	}

	/** Function to render lattices. Currently unsupported */
	function renderLattice() {
		alert("Lattices are not currently supported.");
		return null;
	}

	if (props.kind === TokenizationKind.TOKEN_LIST) {
		const activeTokenizationId = _getActiveTokenizationId();
		return (
			<>
				<MouseDragTokenContext.Provider
					value={{
						mouseDragging: mouseDragging,
						setMouseDragging: setMouseDragging,
						shiftMouseDragging: shiftMouseDragging,
						setShiftMouseDragging: setShiftMouseDragging,
						tokenizationId: props.uuid,
						activeTokenizationIds:
							activeTokenizationId !== null
								? [activeTokenizationId]
								: [],
						setDragStartTokenIndex: setDragStartTokenIndex,
						setDragEndTokenIndex: setDragEndTokenIndex,
						dragStartTokenIndex: dragStartTokenIndex,
						dragEndTokenIndex: dragEndTokenIndex,
					}}>
					{renderList()}
				</MouseDragTokenContext.Provider>
			</>
		);
	} else {
		return renderLattice();
	}
}

export default Tokenization;
